import { Component, OnInit } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { AdsSettingsService } from '../../ads-settings-service.service';

@Component({
  selector: 'app-ads-shop-selection',
  templateUrl: './ads-shop-selection.component.html',
  styleUrl: './ads-shop-selection.component.scss',
  standalone: false
})
export class AdsShopSelectionComponent implements OnInit {
  searchTerm = '';
  showSelectedOnly = false;
  shopListData: any[] = [];
  isDirty = false;

  // Tree expand states
  expandAllShops = true;
  expandSushiShops = false;
  expandBentoShops = false;

  // Checkbox states
  allShopsChecked = false;
  allShopsIndeterminate = false;
  sushiShopsChecked = false;
  sushiShopsIndeterminate = false;
  bentoShopsChecked = false;
  bentoShopsIndeterminate = false;

  shopChecked: Record<string, boolean> = {};

  constructor(private adsSettingService: AdsSettingsService) { }

  ngOnInit(): void {
    this.shopList();
  }

  get filteredShops() {
    if (!this.shopListData?.length) return [];

    let shops = this.shopListData;
    if (this.showSelectedOnly) {
      shops = shops.filter((shop: any) => this.shopChecked[shop.id]);
    }

    if (!this.searchTerm) return shops;

    const term = this.searchTerm.toLowerCase();
    return shops.filter((shop: any) =>
      (shop.name || '').toLowerCase().includes(term) ||
      (shop.code || '').toLowerCase().includes(term) ||
      (shop.address || '').toLowerCase().includes(term)
    );
  }

  get sushiShops() {
    return this.filteredShops.filter((shop: any) => shop.shop_type === 'SUSHI');
  }

  get bentoShops() {
    return this.filteredShops.filter((shop: any) => shop.shop_type === 'BENTO');
  }

  get allShops() {
    return this.filteredShops;
  }

  get selectedShops() {
    return this.allShops.filter((shop: any) => this.shopChecked[shop.id]);
  }

  get selectedSushiShopsCount() {
    return this.sushiShops.filter((shop: any) => this.shopChecked[shop.id]).length;
  }

  get selectedBentoShopsCount() {
    return this.bentoShops.filter((shop: any) => this.shopChecked[shop.id]).length;
  }

  get selectedAllShopsCount() {
    return this.selectedShops.length;
  }

  get selectedShopTypes() {
    const allowed = ['SUSHI', 'BENTO'];
    const types = this.selectedShops
      .map((shop: any) => shop.shop_type)
      .filter((type: string) => allowed.includes(type));
    return Array.from(new Set(types));
  }

  toggleAllShops(event: MatCheckboxChange) {
    const checked = event.checked;
    this.allShops.forEach((shop: any) => (this.shopChecked[shop.id] = checked));
    this.isDirty = true;
    this.updateSelectionStates();
  }

  toggleSushiShops(event: MatCheckboxChange) {
    const checked = event.checked;
    this.sushiShops.forEach((shop: any) => (this.shopChecked[shop.id] = checked));
    this.isDirty = true;
    this.updateSelectionStates();
  }

  toggleBentoShops(event: MatCheckboxChange) {
    const checked = event.checked;
    this.bentoShops.forEach((shop: any) => (this.shopChecked[shop.id] = checked));
    this.isDirty = true;
    this.updateSelectionStates();
  }

  toggleShop(shop: any, event: MatCheckboxChange) {
    this.shopChecked[shop.id] = event.checked;
    this.isDirty = true;
    this.updateSelectionStates();
  }

  setSelectedShops(shopIds: any[]) {
    this.shopChecked = {};
    shopIds.forEach(id => {
      this.shopChecked[id.toString()] = true;
    });
    this.isDirty = false;
    this.updateSelectionStates();
  }

  private updateSelectionStates() {
    const sushiIds = this.sushiShops.map((s: any) => s.id);
    const sushiSelected = sushiIds.filter((id: any) => this.shopChecked[id]);
    this.sushiShopsChecked = sushiSelected.length === sushiIds.length && sushiIds.length > 0;
    this.sushiShopsIndeterminate = sushiSelected.length > 0 && sushiSelected.length < sushiIds.length;

    const bentoIds = this.bentoShops.map((s: any) => s.id);
    const bentoSelected = bentoIds.filter((id: any) => this.shopChecked[id]);
    this.bentoShopsChecked = bentoSelected.length === bentoIds.length && bentoIds.length > 0;
    this.bentoShopsIndeterminate = bentoSelected.length > 0 && bentoSelected.length < bentoIds.length;

    const allIds = this.allShops.map((s: any) => s.id);
    const allSelected = allIds.filter((id: any) => this.shopChecked[id]);
    this.allShopsChecked = allSelected.length === allIds.length && allIds.length > 0;
    this.allShopsIndeterminate = allSelected.length > 0 && allSelected.length < allIds.length;
  }

  private shopList(): void {
    const requestData = {
      search: {
        filters: { filter: [], scope: { offset: 0, limit: 1000000 } },
        sort: [{ column: 'shop.name', order: 'ASC' }]
      }
    };

    this.adsSettingService.getShopList(requestData).subscribe({
      next: (response: any) => {
        this.shopListData = response?.data?.shops || [];
        this.updateSelectionStates();
      },
      error: (error: any) => {
        console.error('Error fetching shops:', error);
      }
    });
  }
}
